import { Box, Typography, Select, MenuItem, useMediaQuery } from '@mui/material';
import React, { useState, useMemo, useEffect } from 'react';
import GalleryCard from '../galleryCard/GalleryCard';
import AIAvatar from '../../assets/svg/AIAvatar';
import { useHiddenItems } from '../../hooks/useHideSearchedProperty/useHiddenItems';
import { useTranslation } from 'react-i18next';
import UpgradeModal from '../modals/upgradeModal';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../theme/theme';
import { jsonData } from '../../db';
import { useDispatch, useSelector } from 'react-redux';
import { selectSubscriptions, getSubscription } from '../../store/slices/subscriptionsSlice';

const options = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  weekday: 'short'
};

export const Gallery = ({ gallery }) => {
  const { t } = useTranslation();
  const { isItemHidden } = useHiddenItems();
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [filter, setFilter] = useState('recently_matched');
  const date = new Date();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  function handleChangeFilter(e) {
    const newFilter = e.target.value;
    setFilter(newFilter);
  }

  const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);

  const sortList = (list) => {
    const sorted = [...list];
    switch (filter) {
      case 'recently_matched':
        return sorted.sort((a, b) => new Date(b.matchedAt) - new Date(a.matchedAt));
      case 'closest_match_score':
        return sorted.sort((a, b) => b.matchScore - a.matchScore);
      case 'lowest_price':
        return sorted.sort((a, b) => a.price - b.price);
      case 'most_recent_listings':
        return sorted.sort((a, b) => new Date(b.listedAt) - new Date(a.listedAt));
      default:
        return sorted;
    }
  };

  const firstList = useMemo(() => sortList(gallery.slice(0, 2)), [gallery, filter]);
  const secondList = useMemo(() => sortList(gallery.slice(2)), [gallery, filter]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '35px',
        padding: isMobile ? 2 : '20px',
        width: '100%',
        height: '100%',
        overflowY: 'scroll',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none'
        }
      }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
          <Typography variant="h6">{t('Search feed')}</Typography>
          <Select value={filter} onChange={handleChangeFilter} variant="outlined" size="small">
            <MenuItem value="recently_matched">{t('Recently Matched')}</MenuItem>
            <MenuItem value="closest_match_score">{t('Closest Match Score')}</MenuItem>
            <MenuItem value="lowest_price">{t('Lowest Price')}</MenuItem>
            <MenuItem value="most_recent_listings">{t('Most Recent Listings')}</MenuItem>
          </Select>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: isMobile ? 'flex-end' : 'center',
            justifyContent: isMobile ? 'flex-start' : 'space-between',
            flexDirection: isMobile ? 'column' : 'row'
          }}>
          <Typography sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <AIAvatar
              sx={{
                mt: 0,
                p: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px'
              }}
            />
            {t('Available Property for you. You can click on the card for more details.')}
          </Typography>
          <Typography variant="body1" sx={{ fontSize: 12, color: '#646464', mt: isMobile ? 1 : 0 }}>
            {formattedDate}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3
        }}>
        {firstList.map(
          (item, index) =>
            !isItemHidden(item.id) && (
              <Box
                key={index}
                sx={{
                  flexGrow: 1,
                  position: 'relative',
                  display: 'flex'
                }}>
                <GalleryCard index={index} item={item} setOpenPaymentModal={setOpenPaymentModal} />
              </Box>
            )
        )}
      </Box>

      {secondList.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: isMobile ? 'flex-end' : 'center',
            justifyContent: isMobile ? 'flex-start' : 'space-between',
            flexDirection: isMobile ? 'column' : 'row'
          }}>
          <Typography sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <AIAvatar
              sx={{
                mt: 0,
                p: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px'
              }}
            />
            {t('Look, we found more options for you. Check it out')}
          </Typography>
          <Typography variant="body1" sx={{ fontSize: 12, color: '#646464', mt: isMobile ? 1 : 0 }}>
            {formattedDate}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3
        }}>
        {secondList.map(
          (item, index) =>
            !isItemHidden(item.id) && (
              <Box
                key={index + 2}
                sx={{
                  flexGrow: 1,
                  position: 'relative',
                  display: 'flex'
                }}>
                <GalleryCard
                  index={index + 2}
                  item={item}
                  setOpenPaymentModal={setOpenPaymentModal}
                />
              </Box>
            )
        )}
      </Box>

      <UpgradeModal
        translate
        currentPlan={jsonData.currentPlan}
        upgradePlan={jsonData.upgradePlan}
        open={openPaymentModal}
        onClose={() => setOpenPaymentModal(false)}
      />
    </Box>
  );
};
